import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft as ArrowLeft, FiCopy as Copy, FiCheck as Check, FiClock as Clock, FiUser as User, FiActivity as Activity, FiAlertCircle as AlertCircle,
  FiDollarSign as DollarSign, FiHash as Hash, FiZap as Zap, FiPlayCircle as PlayCircle, FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiCode as Code, FiDatabase as Database,
  FiActivity as Flame, FiBarChart2 as BarChart2, FiFileText as FileText
} from 'react-icons/fi';
import { getAgentRun, getAgentRunSteps, assignRunProject, assignRunWorkload } from '../../../../services/aiOperationsApi';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import FlamegraphComponent, { transformStepsToFlamegraph } from '../components/visualizations/FlamegraphComponent';
import WaterfallChartComponent, { transformStepsToWaterfall } from '../components/visualizations/WaterfallChartComponent';

interface AgentRun {
  id: string;
  agent_name?: string;
  status?: string;
  outcome?: string;
  user_id?: string;
  session_id?: string;
  duration_seconds?: number;
  cost?: number;
  input_tokens?: number;
  output_tokens?: number;
  start_time?: string;
  end_time?: string;
  metadata_json?: string;
  project_id?: string;
  workload_id?: string;
}

interface AgentStep {
  id?: string;
  step_number?: number;
  tool_used?: string;
  action?: string;
  status?: string;
  input?: string | Record<string, unknown>;
  output?: string | Record<string, unknown>;
  duration_seconds?: number;
  input_tokens?: number;
  output_tokens?: number;
  cost?: number;
}

const AgentRunDetailPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [run, setRun] = useState<AgentRun | null>(null);
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  useEffect(() => {
    loadRunDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const loadRunDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch run details
      const runData = await getAgentRun(runId!);
      setRun(runData.run || runData);
      setSteps(runData.steps || []);

      // If steps not included, fetch separately
      if (!runData.steps) {
        const stepsData = await getAgentRunSteps(runId!);
        setSteps(stepsData.steps || []);
      }
    } catch (err: unknown) {
      console.error('Error loading run details:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleBack = () => {
    navigate('/ai-operations/runs');
  };

  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${minutes}m ${secs}s`;
  };

  const getOutcomeColor = (outcome: string | undefined): string => {
    switch (outcome?.toLowerCase()) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'error':
      case 'failure':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'timeout':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'partial_success':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string | undefined): React.ReactElement => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'running':
        return <PlayCircle className="w-5 h-5 text-yellow-600 animate-pulse" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  // Derive step type from tool_used or action
  const deriveStepType = (step: AgentStep): string => {
    const toolUsed = step.tool_used?.toLowerCase() || '';
    const action = step.action?.toLowerCase() || '';

    if (toolUsed) {
      if (toolUsed.includes('llm') || toolUsed.includes('generate') || toolUsed.includes('chat')) {
        return 'llm';
      }
      if (toolUsed.includes('memory') || toolUsed.includes('retrieve')) {
        return 'memory';
      }
      return 'tool';
    }

    if (action.includes('generate') || action.includes('llm')) return 'llm';
    if (action.includes('tool') || action.includes('execute')) return 'tool';
    if (action.includes('observe') || action.includes('result')) return 'observation';

    return 'action';
  };

  const getStepIcon = (step: AgentStep): React.ReactElement => {
    const stepType = deriveStepType(step);
    switch (stepType) {
      case 'llm':
        return <Code className="w-4 h-4 text-blue-600" />;
      case 'tool':
        return <Zap className="w-4 h-4 text-purple-600" />;
      case 'memory':
        return <Database className="w-4 h-4 text-green-600" />;
      case 'observation':
        return <Database className="w-4 h-4 text-green-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStepTypeLabel = (step: AgentStep): string => {
    if (step.tool_used) return step.tool_used;
    const type = deriveStepType(step);
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getMetadata = (): Record<string, unknown> => {
    try {
      return run?.metadata_json ? JSON.parse(run.metadata_json) : {};
    } catch {
      return {};
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Run not found
        </div>
      </div>
    );
  }

  const metadata = getMetadata();

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Runs
        </button>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {run.agent_name || 'Agent Run'}
            </h1>
            <div className="mt-2 flex items-center space-x-2">
              <code className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                {run.id}
              </code>
              <button
                onClick={() => handleCopy(run.id, 'id')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {copiedField === 'id' ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(`/ai-operations/traces/${run.id}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              View Trace
            </button>
            {getStatusIcon(run.status)}
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              run.status === 'completed'
                ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
            }`}>
              {run.status}
            </span>
            {run.outcome && (
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getOutcomeColor(run.outcome)}`}>
                {run.outcome}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* User */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">User</div>
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {run.user_id || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Session */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Session</div>
            <div className="flex items-center">
              <Activity className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
              <span className="text-gray-900 dark:text-white font-medium truncate" title={run.session_id}>
                {run.session_id?.split('-').slice(-2).join('-') ?? 'N/A'}
              </span>
            </div>
          </div>

          {/* Duration */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Duration</div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-orange-600 dark:text-orange-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {formatDuration(run.duration_seconds)}
              </span>
            </div>
          </div>

          {/* Cost */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Cost</div>
            <div className="flex items-center">
              <DollarSign className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                ${Number(run.cost || 0).toFixed(4)}
              </span>
            </div>
          </div>

          {/* Project */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Project</div>
            <div className="flex items-center">
              <ProjectAssignmentSelect
                currentProjectId={run.project_id}
                onAssign={async (projectId) => {
                  await assignRunProject(run.id, projectId);
                  setRun((prev: AgentRun | null) => prev ? { ...prev, project_id: projectId ?? undefined } : prev);
                }}
              />
            </div>
          </div>

          {/* Workload */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Workload</div>
            <div className="flex items-center">
              <WorkloadAssignmentSelect
                currentWorkloadId={run.workload_id}
                onAssign={async (workloadId) => {
                  await assignRunWorkload(run.id, workloadId);
                  setRun((prev: AgentRun | null) => prev ? { ...prev, workload_id: workloadId ?? undefined } : prev);
                }}
              />
            </div>
          </div>
        </div>

        {/* Tokens */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Input Tokens</div>
              <div className="flex items-center">
                <Hash className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                <span className="text-gray-900 dark:text-white font-medium">
                  {run.input_tokens?.toLocaleString() || 0}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Output Tokens</div>
              <div className="flex items-center">
                <Hash className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                <span className="text-gray-900 dark:text-white font-medium">
                  {run.output_tokens?.toLocaleString() || 0}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Tokens</div>
              <div className="flex items-center">
                <Hash className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                <span className="text-gray-900 dark:text-white font-medium">
                  {((run.input_tokens || 0) + (run.output_tokens || 0)).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Started At</div>
              <div className="text-gray-900 dark:text-white">
                {formatTimestamp(run.start_time)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Ended At</div>
              <div className="text-gray-900 dark:text-white">
                {formatTimestamp(run.end_time)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Steps ({steps.length})
            </button>
            <button
              onClick={() => setActiveTab('flamegraph')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'flamegraph'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <Flame className="w-4 h-4 mr-1" />
              Trace Flamegraph
            </button>
            <button
              onClick={() => setActiveTab('waterfall')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'waterfall'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <BarChart2 className="w-4 h-4 mr-1" />
              Trace Waterfall
            </button>
            <button
              onClick={() => setActiveTab('metadata')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'metadata'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Metadata
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Steps Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {steps.length === 0 ? (
                <div className="text-center text-gray-600 dark:text-gray-400 py-8">
                  No steps recorded for this run
                </div>
              ) : (
                steps.map((step, index) => (
                  <div
                    key={step.id || index}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 text-xs font-semibold">
                          {step.step_number || index + 1}
                        </span>
                        {getStepIcon(step)}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {getStepTypeLabel(step)}
                        </span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        step.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                      }`}>
                        {step.status}
                      </span>
                    </div>

                    {/* Action description */}
                    {step.action && (
                      <div className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                        {step.action}
                      </div>
                    )}

                    {step.input && (
                      <div className="mb-2">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Input:</div>
                        <div className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded p-2 max-h-32 overflow-auto font-mono text-xs">
                          {typeof step.input === 'string' ? step.input : JSON.stringify(step.input, null, 2)}
                        </div>
                      </div>
                    )}

                    {step.output && (
                      <div className="mb-2">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Output:</div>
                        <div className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded p-2 max-h-32 overflow-auto font-mono text-xs">
                          {typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {step.duration_seconds != null && step.duration_seconds > 0 && (
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDuration(step.duration_seconds)}
                        </span>
                      )}
                      {step.input_tokens != null && (
                        <span className="flex items-center">
                          <Hash className="w-3 h-3 mr-1" />
                          {step.input_tokens}↓ / {step.output_tokens || 0}↑
                        </span>
                      )}
                      {step.cost != null && step.cost > 0 && (
                        <span className="flex items-center">
                          <DollarSign className="w-3 h-3 mr-1" />
                          ${Number(step.cost).toFixed(4)}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Flamegraph Tab */}
          {activeTab === 'flamegraph' && (
            <FlamegraphComponent
              data={transformStepsToFlamegraph(steps, run?.duration_seconds ?? 0)}
              width={1000}
              height={400}
              onExport={() => undefined}
            />
          )}

          {/* Waterfall Tab */}
          {activeTab === 'waterfall' && (
            <WaterfallChartComponent
              spans={transformStepsToWaterfall(steps, run?.start_time)}
              runStartTime={run?.start_time}
              totalDuration={run?.duration_seconds}
              height={500}
              onExport={() => undefined}
            />
          )}

          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <pre className="text-sm text-gray-900 dark:text-white overflow-auto">
                {JSON.stringify(metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentRunDetailPage;
